/**
 * Participation Log Panel (Phase 2 — Step 2.6).
 *
 * Per-session quality rating (1–5 stars) + freeform notes.
 */

import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useAppStore } from '@/store';
import { selectParticipationAttendanceCorrelation } from '@/store/selectors';
import { Button, EmptyState } from '../shared';
import { todayISO } from '@abs/shared-logic';
import type { ParticipationEntry } from '@abs/shared-types';

interface ParticipationLogPanelProps {
  courseId: string;
}

export function ParticipationLogPanel({ courseId }: ParticipationLogPanelProps): React.ReactElement {
  const store = useAppStore;
  const entries = store.getState().participationEntries.filter((e) => e.courseId === courseId);
  const correlation = selectParticipationAttendanceCorrelation(store.getState(), courseId);

  const [date, setDate] = useState(todayISO());
  const [quality, setQuality] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    store.getState().addParticipation({
      courseId,
      date,
      quality,
      notes,
    });
    setNotes('');
    setQuality(3);
  };

  const handleDelete = (id: string) => {
    store.getState().deleteParticipation(id);
  };

  const sortedEntries = [...entries].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="participation-log" role="region" aria-label="Participation Log">
      <h4>Log Participation</h4>
      <form onSubmit={handleSubmit} className="participation-log__form">
        <label>
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label>
          Quality
          <div className="participation-log__stars" role="radiogroup" aria-label="Quality rating">
            {([1, 2, 3, 4, 5] as const).map((star) => (
              <button
                key={star}
                type="button"
                className={`participation-log__star ${star <= quality ? 'participation-log__star--active' : ''}`}
                onClick={() => setQuality(star)}
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
                role="radio"
                aria-checked={star === quality}
              >
                <Star
                  aria-hidden="true"
                  fill={star <= quality ? 'currentColor' : 'none'}
                  className={star <= quality ? 'participation-log__star-icon participation-log__star-icon--active' : 'participation-log__star-icon'}
                />
              </button>
            ))}
          </div>
        </label>
        <label>
          Notes
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How was your participation?"
            rows={2}
          />
        </label>
        <Button variant="primary" type="submit">Log Participation</Button>
      </form>

      <div className="participation-log__history">
        <h5>Recent Entries</h5>
        {entries.length >= 3 && correlation.avgQualityOnTime !== null && correlation.avgQualityLate !== null && (
          <div className="participation-log__insight" role="status">
            <p className="stat-caption">
              Your participation averages {correlation.avgQualityOnTime.toFixed(1)} on on-time days vs {correlation.avgQualityLate.toFixed(1)} on days you arrived late.
            </p>
          </div>
        )}
        {sortedEntries.length === 0 ? (
          <EmptyState title="No participation logged" description="Log your first entry above." />
        ) : (
          sortedEntries.slice(0, 10).map((entry: ParticipationEntry) => (
            <div key={entry.id} className="participation-log__entry">
              <span className="participation-log__entry-date">{entry.date}</span>
              <span className="participation-log__entry-stars" aria-label={`${entry.quality} of 5 stars`}>
                {([1, 2, 3, 4, 5] as const).map((s) => (
                  <Star
                    key={s}
                    aria-hidden="true"
                    fill={s <= entry.quality ? 'currentColor' : 'none'}
                    className={s <= entry.quality ? 'participation-log__star-icon participation-log__star-icon--active' : 'participation-log__star-icon'}
                  />
                ))}
              </span>
              {entry.notes && <span className="participation-log__entry-notes">{entry.notes}</span>}
              <button
                className="participation-log__entry-delete"
                onClick={() => handleDelete(entry.id)}
                aria-label={`Delete participation entry for ${entry.date}`}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
